import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';

import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { ContasReceberService } from '../../../../../shared/services/apis/financas/contas-receber.service';
import { TagContasReceber } from '../../../../../shared/services/models/financas/tag-contas-receber.model';
import {
  AcaoTagsEnum,
  ContasReceberBulkTagsRequest,
} from '../../../../../shared/services/requests/financas/contas-receber.request';

export interface AlterarTagsDialogData {
  idsContasReceberParcela: number[];
  tags: TagContasReceber[];
  idsTagsAtuais?: number[];
}

@Component({
  selector: 'app-cr-alterar-tags-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatInputModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './alterar-tags-dialog.component.html',
  styleUrls: ['./alterar-tags-dialog.component.css'],
})
export class AlterarTagsDialogComponent implements OnInit {
  public modo: string = 'incluir';
  public searchText: string = '';
  public tagsSelecionados: Set<number> = new Set();
  public tagsFiltrados: TagContasReceber[] = [];
  public isLoading: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<AlterarTagsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AlterarTagsDialogData,
    private contasReceberService: ContasReceberService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.tagsFiltrados = [...this.data.tags];
    this.tagsSelecionados = new Set(this.data.idsTagsAtuais ?? []);
  }

  public filtrarTags(): void {
    const termo = this.searchText.toLowerCase().trim();
    if (!termo) {
      this.tagsFiltrados = [...this.data.tags];
    } else {
      this.tagsFiltrados = this.data.tags.filter(m => m.descricao.toLowerCase().includes(termo));
    }
  }

  public toggleTag(id: number): void {
    if (this.tagsSelecionados.has(id)) {
      this.tagsSelecionados.delete(id);
    } else {
      this.tagsSelecionados.add(id);
    }
  }

  public isTagSelecionado(id: number): boolean {
    return this.tagsSelecionados.has(id);
  }

  public get contadorText(): string {
    const count = this.tagsSelecionados.size;
    if (count === 0) return '';
    return count === 1 ? '1 tag selecionado' : `${count} tags selecionados`;
  }

  public get helpText(): string {
    if (this.modo === 'incluir') {
      return 'Os tags selecionados serão adicionados às parcelas, mantendo os existentes.';
    }
    return 'Os tags selecionados serão removidos das parcelas, mantendo os demais existentes.';
  }

  public get podeAplicar(): boolean {
    return this.tagsSelecionados.size > 0 && !this.isLoading;
  }

  public onModoChange(): void {
    this.tagsSelecionados.clear();
  }

  public salvar(): void {
    if (!this.podeAplicar) return;

    this.isLoading = true;

    const request: ContasReceberBulkTagsRequest = {
      idsContasReceberParcela: this.data.idsContasReceberParcela,
      acao: this.modo === 'incluir' ? AcaoTagsEnum.Incluir : AcaoTagsEnum.Remover,
      idsTags: Array.from(this.tagsSelecionados),
    };

    this.contasReceberService.alterarTagsEmMassa(request).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          return;
        }
        const mensagem =
          this.modo === 'incluir' ? 'Tags incluídos com sucesso!' : 'Tags removidos com sucesso!';
        this.alertService.show('Sucesso', mensagem, AlertType.success);
        this.dialogRef.close({ success: true });
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Falha ao atualizar os tags.', AlertType.error);
      },
    });
  }

  public fechar(): void {
    this.dialogRef.close();
  }
}
